import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useFetchFeatureFlags,
  useFetchFields,
  useFetchGames,
  useFetchRanking,
} from "../api/hooks";
import { FieldsFieldsType } from "../api/types";
import { CategoryType, PoolGamesType, PoolRankingType } from "../types";

type RootContextType = {
  selectedCategory?: CategoryType;
  selectedPool?: string;
  ranking?: PoolRankingType[];
  games?: PoolGamesType[];
  dayPart?: "am" | "pm";
  fields?: FieldsFieldsType[];
  featureFlags?: Record<string, boolean>;
  setSelectedCategory: (category: CategoryType) => void;
  setSelectedPool: (pool?: string) => void;
  setDayPart: (dayPart: "am" | "pm") => void;
};

export const RootContext = createContext<RootContextType>({
  selectedCategory: "Masculins",
  selectedPool: undefined,
  ranking: undefined,
  games: undefined,
  dayPart: "am",
  fields: undefined,
  featureFlags: undefined,
  setSelectedCategory: (category: CategoryType) => {},
  setSelectedPool: (pool?: string) => {},
  setDayPart: (dayPart: "am" | "pm") => {},
});

export const RootProvider = ({ children }: PropsWithChildren) => {
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryType>("Masculins");
  const [selectedPool, setSelectedPool] = useState<string>();
  const [dayPart, setDayPart] = useState<"am" | "pm">("am");

  const { data: ranking, refetch: refetchRanking } = useFetchRanking({
    selectedCategory,
    dayPart,
  });
  const { data: games, refetch: refetchGames } = useFetchGames({
    selectedCategory,
    dayPart,
  });
  const { data: fields } = useFetchFields();
  const { data: featureFlags } = useFetchFeatureFlags();

  useEffect(() => {
    refetchRanking();
    refetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayPart]);

  const value = useMemo(
    () => ({
      selectedCategory,
      selectedPool,
      ranking,
      games,
      dayPart,
      fields,
      featureFlags,
      setSelectedCategory,
      setSelectedPool,
      setDayPart,
    }),
    [
      selectedCategory,
      selectedPool,
      ranking,
      games,
      dayPart,
      fields,
      featureFlags,
    ]
  );

  return <RootContext.Provider value={value}>{children}</RootContext.Provider>;
};
